import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [authDisabled, setAuthDisabled] = useState(false);

  useEffect(() => {
    // One-time cleanup of any legacy token; auth now relies solely on httpOnly cookies.
    localStorage.removeItem("bc_token");
    (async () => {
      try {
        const cfg = await api.get("/auth/config");
        setAuthDisabled(!!cfg.data.auth_disabled);
      } catch (e) { console.error("Failed to load auth config", e); }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    api.get("/auth/me").then(({ data: me }) => setUser(me)).catch(() => {});
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); }
    setUser(false);
  }, []);

  const savePrefs = useCallback(async (prefs) => {
    const { data } = await api.put("/auth/preferences", prefs);
    setUser((u) => (u ? { ...u, preferences: data.preferences } : u));
    return data.preferences;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, savePrefs, authDisabled }),
    [user, login, logout, savePrefs, authDisabled],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
